/*
 * Design tokens. Single source of truth for every CSS custom property on the site.
 * GlobalStyles.ts prints these maps into :root, so a value lives here exactly once.
 * No hex literal may appear in any styles.ts: read the CSS var instead.
 */

/* c.1 to c.5: color. Contrast ratios are documented in DESIGN-SPEC (c). */
export const colorVars = {
  /* c.1 paper, kept */
  '--bg': '#FAF7F2',
  '--bg-tint': '#F1ECE3',
  '--surface': '#FFFFFF',
  '--ink': '#201933',
  '--ink-dim': '#4E4763',
  '--ink-mute': '#6F687F',
  '--accent': '#5B2DD9',
  '--accent-deep': '#3D1C99',
  '--accent-soft': 'rgba(91, 45, 217, 0.07)',
  /* c.2 paper, new */
  '--paper-2': '#F4EFE6',
  '--paper-cream': '#FFFDF7',
  '--rule-hair': 'rgba(32, 25, 51, 0.07)',
  '--rule': 'rgba(32, 25, 51, 0.12)',
  '--border': 'rgba(32, 25, 51, 0.12)',
  '--rule-strong': 'rgba(32, 25, 51, 0.24)',
  '--ink-inverse': '#FAF8F2',
  '--receipt-bg': '#F1ECE3',
  /* c.3 product accents (fills, dots, borders only) and their text-safe -ink siblings */
  '--chorie': '#2F9B77',
  '--chorie-ink': '#1F6B51',
  '--chorie-soft': 'rgba(47, 155, 119, 0.08)',
  '--findy': '#E4593B',
  '--findy-ink': '#B23C22',
  '--findy-soft': 'rgba(228, 89, 59, 0.07)',
  '--saida': '#C9A24B',
  '--saida-ink': '#7A5D1F',
  '--saida-navy': '#0D2440',
  '--saida-deep': '#8F6F2A',
  '--saida-soft': 'rgba(201, 162, 75, 0.09)',
  '--r407-green-ink': '#1F4A34',
  '--r407-ochre-ink': '#A04718',
  '--imgr-gold-ink': '#7A5410',
  /* c.4 night: one canonical ground, two instrument grounds */
  '--night-0': '#070510',
  '--night-1': '#100C1E',
  '--night-ink': '#F5F2EC',
  '--night-dim': '#A8A2B8',
  '--night-mute': '#8A8499',
  '--night-rule-hair': 'rgba(245, 242, 236, 0.06)',
  '--night-rule': 'rgba(245, 242, 236, 0.10)',
  '--night-rule-strong': 'rgba(245, 242, 236, 0.22)',
  '--violet-bright': '#A78BFA',
  '--violet-soft': 'rgba(167, 139, 250, 0.10)',
  '--radar-panel': '#0B1613',
  '--radar-panel-2': '#12211B',
  '--radar-line': '#24352D',
  '--radar-faint': '#8BA396',
  '--r407-panel': '#0F110E',
  '--r407-panel-2': '#17160F',
  '--r407-line': '#2A2E26',
  '--r407-faint': '#9AA08F',
  '--panel-inset': 'rgba(255, 255, 255, 0.04)',
  '--radar-gold': '#E8C66A',
  '--radar-gold-deep': '#C08B2D',
  '--radar-teal': '#4FBD9A',
  '--radar-grey': '#7D9DB0',
  '--r407-green': '#6FD39A',
  '--r407-ochre': '#F0A35E',
  '--r407-amber': '#E8B84B',
  /* c.5 focus and text-accent indirections. NightBand re-points both. */
  '--focus-ring': 'var(--accent)',
  '--accent-text': 'var(--accent)',
  '--selection-bg': 'var(--accent)',
  '--selection-ink': '#FFFFFF',
  /* nav: read by TopBar, SectionRail and AskDock; html[data-band="night"] overrides them */
  '--nav-bg': 'rgba(250, 247, 242, 0.82)',
  '--nav-bg-solid': '#FAF7F2',
  '--nav-ink': '#201933',
  /* Picked against the worst 0.82-alpha composite (the paper bar over a night band, rgb(206,203,201)),
     not against --nav-bg-solid: 4.80:1 there, 7.20:1 on solid paper. */
  '--nav-mute': '#565064',
  '--nav-rule': 'rgba(32, 25, 51, 0.12)',
  '--nav-accent': '#5B2DD9',
  /* elevation: used only on the Saída case file and the HomeChores phone screen */
  '--shadow-card': '0 1px 2px rgba(32, 25, 51, 0.04), 0 12px 32px rgba(32, 25, 51, 0.06)',
  '--shadow-card-hover': '0 2px 4px rgba(32, 25, 51, 0.05), 0 20px 48px rgba(32, 25, 51, 0.1)',
} as const;

/* Night overrides for the nav vars, applied on html[data-band="night"] by the active-section observer. */
export const nightNavVars = {
  '--nav-bg': 'rgba(7, 5, 16, 0.82)',
  '--nav-bg-solid': '#070510',
  '--nav-ink': '#F5F2EC',
  /* Same rule the other way: 5.09:1 on the night bar composited over paper, 7.99:1 on solid night. */
  '--nav-mute': '#A5A0B5',
  '--nav-rule': 'rgba(245, 242, 236, 0.10)',
  '--nav-accent': '#A78BFA',
  /* The fixed nav keeps the paper ring otherwise, which is 2.69:1 on night (spec c.5 wants this one). */
  '--focus-ring': 'var(--violet-bright)',
} as const;

/* c.6 type scale. Three families only. */
export const fontVars = {
  '--font-display': "'Instrument Serif', Georgia, 'Times New Roman', serif",
  '--font-body': "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  '--font-mono': "'JetBrains Mono', 'Geist Mono', 'SFMono-Regular', Menlo, monospace",
} as const;

export const typeVars = {
  '--t-mega': 'clamp(40px, 6.4vw, 92px)',
  '--lh-mega': '0.98',
  '--track-mega': '-0.025em',
  '--t-figure': 'clamp(44px, 5.2vw, 76px)',
  '--lh-figure': '0.9',
  '--track-figure': '-0.02em',
  '--t-h1': 'clamp(32px, 4.4vw, 56px)',
  '--lh-h1': '1.05',
  '--t-h2': '32px',
  '--lh-h2': '1.15',
  '--t-h3': '20px',
  '--lh-h3': '1.25',
  '--t-lede': '19px',
  '--lh-lede': '1.6',
  '--t-body': '18px',
  '--lh-body': '1.65',
  '--t-small': '15px',
  '--lh-small': '1.6',
  '--t-data': '28px',
  '--lh-data': '1',
  '--t-meta': '12px',
  '--lh-meta': '1.4',
  '--track-meta': '0.1em',
  '--t-micro': '11px',
  '--lh-micro': '1.3',
  '--track-micro': '0.16em',
  '--t-caption': '12px',
  '--lh-caption': '1.5',
  '--track-caption': '0.04em',
} as const;

/* Step-downs below 640px (c.6). */
export const typeVarsMobile = {
  '--t-h2': '26px',
  '--t-body': '17px',
} as const;

/* e.1 easings and e.2 duration ladder. No ad-hoc numbers anywhere else. */
export const motionVars = {
  '--ease-out': 'cubic-bezier(0.16, 1, 0.3, 1)',
  '--ease-swap': 'cubic-bezier(0.65, 0, 0.35, 1)',
  '--ease-quick': 'cubic-bezier(0.4, 0, 0.2, 1)',
  '--ease-stamp': 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  '--d-micro': '160ms',
  '--d-ui': '260ms',
  '--d-el': '340ms',
  '--d-swap': '520ms',
  '--d-section': '640ms',
  '--d-count': '900ms',
  '--d-draw': '1400ms',
} as const;

/* Same ladder as numbers, for rAF and setTimeout consumers. */
export const durations = {
  micro: 160,
  ui: 260,
  el: 340,
  swap: 520,
  section: 640,
  count: 900,
  draw: 1400,
} as const;

/* c.7 spacing, containers, radius. c.8 breakpoints. */
export const layoutVars = {
  '--gutter': '24px',
  '--section-pad': 'clamp(96px, 11vh, 128px)',
  '--section-pad-mobile': '56px',
  '--scroll-margin': '88px',
  '--nav-h': '56px',
  '--container': '1200px',
  '--container-instrument': '1320px',
  '--container-document': '980px',
  '--container-reading': '880px',
  '--container-ask': '1000px',
  '--prose-max': '62ch',
  '--radius-card': '16px',
  '--radius-instrument': '14px',
  '--radius-night': '12px',
  '--radius-pill': '999px',
  '--doc-progress': '0',
} as const;

export const breakpoints = {
  rail: 1280,
  twoColumn: 1024,
  navCollapse: 1100,
  compact: 640,
  narrow: 400,
} as const;

/* Media query helpers for styles.ts files: `${media.compact} { ... }` */
export const media = {
  rail: `@media (min-width: ${breakpoints.rail}px)`,
  belowRail: `@media (max-width: ${breakpoints.rail - 1}px)`,
  twoColumn: `@media (max-width: ${breakpoints.twoColumn}px)`,
  navCollapse: `@media (max-width: ${breakpoints.navCollapse}px)`,
  compact: `@media (max-width: ${breakpoints.compact}px)`,
  narrow: `@media (max-width: ${breakpoints.narrow}px)`,
  coarse: '@media (pointer: coarse)',
  noHover: '@media (hover: none)',
  reducedMotion: '@media (prefers-reduced-motion: reduce)',
} as const;

export type ColorVar = keyof typeof colorVars;
export type CssVar =
  | ColorVar
  | keyof typeof fontVars
  | keyof typeof typeVars
  | keyof typeof motionVars
  | keyof typeof layoutVars;

/* Back-compat typed palette (theme.ts reads `tokens` and `type`; both are kept). */
export const tokens = {
  bg: colorVars['--bg'],
  bgTint: colorVars['--bg-tint'],
  surface: colorVars['--surface'],
  ink: colorVars['--ink'],
  inkDim: colorVars['--ink-dim'],
  inkMute: colorVars['--ink-mute'],
  accent: colorVars['--accent'],
  accentDeep: colorVars['--accent-deep'],
  paper2: colorVars['--paper-2'],
  paperCream: colorVars['--paper-cream'],
  inkInverse: colorVars['--ink-inverse'],
  chorie: colorVars['--chorie'],
  chorieInk: colorVars['--chorie-ink'],
  findy: colorVars['--findy'],
  findyInk: colorVars['--findy-ink'],
  saida: colorVars['--saida'],
  saidaInk: colorVars['--saida-ink'],
  saidaNavy: colorVars['--saida-navy'],
  night0: colorVars['--night-0'],
  night1: colorVars['--night-1'],
  nightInk: colorVars['--night-ink'],
  nightDim: colorVars['--night-dim'],
  nightMute: colorVars['--night-mute'],
  violetBright: colorVars['--violet-bright'],
  border: colorVars['--border'],
} as const;

export const type = {
  families: {
    display: fontVars['--font-display'],
    body: fontVars['--font-body'],
    mono: fontVars['--font-mono'],
  },
  sizes: {
    display: '72px',
    h1: '56px',
    h2: '32px',
    h3: '20px',
    body: '18px',
    small: '14px',
    meta: '12px',
  },
  weights: {
    regular: 400,
    medium: 500,
    bold: 700,
  },
  lineHeight: {
    tight: 0.95,
    snug: 1.05,
    h2: 1.15,
    body: 1.65,
    meta: 1.4,
  },
  tracking: {
    display: '-0.02em',
    eyebrow: '0.08em',
    meta: '0.1em',
  },
} as const;
